/*
 * Copyright (c) 2024 Helvia Technologies PC <contact@helvia.io>
 */

import { useEffect, useState } from 'react';
import { useLazyFetchSharePointDrivesQuery } from '../../../store/knowledgeBases/knowledgeBasesApi';

/**
 * Fetches SharePoint document library (drive) options with debounce.
 *
 * Triggers a fetch when both `consentTenantId` and `siteUrl` are truthy,
 * debounced by 500ms to avoid excessive requests while the user types.
 *
 * @param {boolean} isActive  - Whether the current integration is SharePoint.
 * @param {string}  consentTenantId - Tenant ID obtained from the consent flow.
 * @param {string}  siteUrl   - The SharePoint site URL entered by the user.
 * @returns {{ driveOptions: Array<{value: string, label: string}>, isFetchingDrives: boolean }}
 */
export const useSharePointDrives = (isActive, consentTenantId, siteUrl) => {
  const [fetchDrives, { isFetching: isFetchingDrives }] = useLazyFetchSharePointDrivesQuery();
  const [driveOptions, setDriveOptions] = useState([]);

  useEffect(() => {
    if (!isActive || !consentTenantId || !siteUrl) {
      setDriveOptions([]);
      return;
    }
    const timer = setTimeout(() => {
      fetchDrives({ tenantId: consentTenantId, siteUrl })
        .unwrap()
        .then((result) => {
          setDriveOptions(
            (result?.drives || []).map((d) => ({ value: d.name, label: d.name }))
          );
        })
        .catch(() => {
          setDriveOptions([]);
        });
    }, 500);
    return () => clearTimeout(timer);
  }, [isActive, consentTenantId, siteUrl, fetchDrives]);

  return { driveOptions, isFetchingDrives };
};
